import {
	Button,
	MenuDivider,
	MenuItemCheckbox,
	mergeClasses,
} from "@fluentui/react-components";
import { TableType } from "@/common/types";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useInteractionMenuStyle } from "@/components/molecules/interactionMenu/style";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import { MenuListPopover } from "@/components/atoms/menuList";
import { Queue, InteractionFilterData } from "@/repository/models";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	InteractionFilterActions,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import { ArrowSplitRegular } from "@fluentui/react-icons";
import TooltipWithMount from "@/components/atoms/tooltipWithMount/TooltipWithMount";
import { SupervisorQueueFilterProps } from "./types";
import {
	SupervisorAgentsFilterActions,
	useSupervisorAgentsFilterStore,
} from "@/common/stores/nonpersistingStores/supervisorAgentsFilter";
import { SupervisorAgentsFilterData } from "@/repository/models";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";

export const SupervisorQueueFilter = (props: SupervisorQueueFilterProps) => {
	const { cacheKey, type, filterData } = props;
	const menuClasses = useInteractionMenuStyle();
	const classes = useInteractionStyle();
	const intl = useIntl();
	const queueFilterKey = "queueFilterKey";
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const filterDataActions = useInteractionFilterStore(
		(actions: InteractionFilterActions) => actions
	);
	const supervisorAgentsFilterActions = useSupervisorAgentsFilterStore(
		(actions: SupervisorAgentsFilterActions) => actions
	);

	const getInitialSelectedQueuesByCacheKey = useCallback(() => {
		if (cacheKey && appStoreCache[cacheKey]) {
			return appStoreCache[cacheKey].queueIds;
		} else {
			return [];
		}
	}, []);

	const [selectedQueues, setSelectedQueues] = useState<string[]>(
		getInitialSelectedQueuesByCacheKey()
	);
	const queueState = useQueuesStore((state: QueuesState) => state);

	const supervisedQueues = useMemo<Queue[]>(
		() =>
			queueState.queues.filter((x) =>
				type == TableType.SupervisorAgents
					? x.isSupervisor || (x.isOperator && x.hasAgentsActivityRight)
					: x.isSupervisor ||
					(x.isOperator && x.hasAllInteractionVisibilityRight)
			),
		[queueState.queues]
	);

	const setFilterData = useCallback((filterData: InteractionFilterData) => {
		switch (type) {
			case TableType.Active:
				filterDataActions.setActive(filterData);
				break;
			case TableType.None:
				filterDataActions.setWaiting(filterData);
				break;
			default:
				break;
		}
	}, [filterDataActions]);

	useEffect(() => {
		cacheKey === CacheKey.SupervisorAgentsFilterData
			? setFilterDataForAgents()
			: setFilterDataForInteractions();
	}, [selectedQueues]);

	useEffect(() => {
		if (queueState.isDataLoaded) {
			setSelectedQueues((prevSelected) =>
				prevSelected.filter((id) => queueState.queues.some((queue) => queue.id === id))
			);
		}
	}, [queueState.queues, queueState.isDataLoaded]);

	const setFilterDataForInteractions = useCallback(() => {
		if (
			cacheKey &&
			((appStoreCache[cacheKey] &&
				selectedQueues !== appStoreCache[cacheKey].queueIds) ||
				!appStoreCache[cacheKey])
		) {
			const filterDataModel = {
				queueIds: selectedQueues,
				types: filterData ? filterData.types : [],
			} as InteractionFilterData;

			appStoreActions.setCache(cacheKey as CacheKey, filterDataModel);

			setFilterData(filterDataModel);
		}
	}, [selectedQueues]);

	const setFilterDataForAgents = useCallback(() => {
		if (
			cacheKey &&
			appStoreCache[cacheKey] &&
			selectedQueues !== appStoreCache[cacheKey].queueIds
		) {
			const filterData = {
				...appStoreCache[cacheKey],
				queueIds: selectedQueues,
				searchText: appStoreCache[cacheKey].searchText ?? "",
			} as SupervisorAgentsFilterData;

			appStoreActions.setCache(cacheKey as CacheKey, filterData);

			supervisorAgentsFilterActions.setQueueIds(selectedQueues);
		}
	}, [selectedQueues]);

	const cleanSelectedQueues = useCallback(() => setSelectedQueues([]), []);

	const manageSelectedQueues = useCallback(
		(queueId: string) => {
			if (selectedQueues.includes(queueId)) {
				setSelectedQueues(selectedQueues.filter((x) => x !== queueId));
			} else {
				setSelectedQueues((prevState) => [...prevState, queueId]);
			}
		},
		[supervisedQueues, selectedQueues]
	);

	notificationListener(ServerEvent.UserMembershipChanged, (queue: Queue) => {
		if (!(queue.isSupervisor || queue.isOperator)) {
			setSelectedQueues((prevSelected) => prevSelected.filter((id) => id !== queue.id));
		}
	});

	notificationListener(ServerEvent.UserMembershipRemoved, (queue: Queue) => {
		setSelectedQueues((prevSelected) =>
			prevSelected.filter((id) => id !== queue.id)
		);
	});

	const filterQueuesItems = useMemo<JSX.Element[]>(
		() => [
			...supervisedQueues.map((queue: Queue, index: number) => (
				<TooltipWithMount
					key={`${queue.id}-${index}`}
					positioning={"above"}
					content={queue.name}
					relationship="label"
				>
					<MenuItemCheckbox
						key={`${queue.id}-${index}`}
						name={queueFilterKey}
						value={queue.id}
						className={mergeClasses(
							menuClasses.filterCheckboxItem,
							classes.leftAlign,
							classes.ellipsis
						)}
						onClick={() => {
							manageSelectedQueues(queue.id);
						}}
					>
						{queue.name}
					</MenuItemCheckbox>
				</TooltipWithMount>
			)),
		],
		[supervisedQueues, selectedQueues]
	);

	const queueFilter = useMemo<JSX.Element>(
		() =>
			type === TableType.Active ||
				type === TableType.None ||
				type === TableType.SupervisorAgents ? (
				<MenuListPopover
					classesName={{
						menuPopover: menuClasses.menuPopover,
						menuList: menuClasses.queueFilterPopoverContent,
						menuHeader: menuClasses.filterGroupHeader,
					}}
					checkedValues={{
						[queueFilterKey]: selectedQueues,
					}}
					positioning="above-end"
					trigger={
						selectedQueues.length ? (
							<Button
								shape="circular"
								onClick={(e) => {
									e.preventDefault();
								}}
								className={mergeClasses(
									classes.filterButton,
									type !== TableType.SupervisorAgents
										? classes.filterMargin
										: classes.filterSlimMargin
								)}
							>
								<div className={classes.filterFontWeight}>
									{selectedQueues.length}
								</div>
								<ArrowSplitRegular className={classes.filterButtonIcon} />
							</Button>
						) : (
							<Button
								appearance="transparent"
								icon={<ArrowSplitRegular />}
								onClick={(e) => {
									e.preventDefault();
								}}
								className={
									type !== TableType.SupervisorAgents
										? classes.filterMargin
										: undefined
								}
							/>
						)
					}
					itemsHeader={[
						<span>
							{intl.formatMessage({ id: "InteractionMenu.ShowOnlyQueues" })}
						</span>,
						<Button
							appearance="transparent"
							className={menuClasses.cleanButtonSize}
							onClick={() => {
								cleanSelectedQueues();
							}}
						>
							{intl.formatMessage({ id: "InteractionMenu.Reset" })}
						</Button>,
					]}
					itemsDivider={<MenuDivider key="interactionDivider" />}
					items={filterQueuesItems}
				/>
			) : (
				<></>
			),
		[supervisedQueues, selectedQueues, type]
	);

	return queueFilter;
};
